import "./hero-tease.scss";
import "./hero-tease-card.scss";
import "./hero-tease-stacked.scss";
import "./hero-tease-side-by-side.scss";

import cn from "clsx";
import {
  MediaAsset,
  MediaAssetProps,
} from "design-system/components/primitives/media-asset/media-asset";

import {
  Byline,
  BylineProps,
} from "design-system/components/primitives/byline/byline";
import { Theme } from "design-system/utils/theme";
import { Alignment, EditAttributes, Layout } from "design-system/types/types";
import { Link } from "design-system/components/primitives/link/link";
import { shouldDecreaseTextSize } from "design-system/utils/text";
import { MouseEventHandler, ReactNode } from "react";
import {
  ComponentHeader,
  ComponentHeaderProps,
} from "design-system/components/primitives/component-header/component-header";

export interface HeroTeaseOverline {
  label?: string;
  link?: string;
}

export interface HeroTeaseProps {
  media?: MediaAssetProps;
  byline?: BylineProps;
  layout?: Exclude<Layout, "vertical" | "horizontal" | "center" | "wide">;
  link?: string;
  overline?: HeroTeaseOverline;
  subheading?: ReactNode;
  title?: string;
  headerAbove?: boolean;
  hasHeader?: boolean;
  theme?: Theme;
  isTopper?: boolean;
  searchPropsSize?: number;
  align?: Alignment;
  header?: ComponentHeaderProps;
  onClick?: MouseEventHandler;
  editAttributes?: {
    title?: EditAttributes;
    subheading?: EditAttributes;
    overline?: EditAttributes;
  };
  hideByline?: boolean;
}

/**
 * The hero tease component is used to tease a piece of content (e.g. a featured story)
 * in a landing page section.
 *
 * ## See it in use on...
 * - The [Editorial Landing Page](iframe.html?id=example-pages-editorial-landing-pages--editorial-landing-page&viewMode=story)
 *
 *
 *
 * ### Card Style
 * - **`id: CB-007-003-00`**
 * - **`data-region: cb__hero-tease__card`**
 */
export function HeroTease({
  media,
  byline,
  layout = "card",
  link,
  overline,
  subheading,
  theme = "dark",
  title,
  hasHeader = false,
  headerAbove = false,
  isTopper = false,
  align = "full",
  header,
  editAttributes,
  hideByline = false,
}: HeroTeaseProps) {
  if (!title || !link) return null;

  const componentHeadingLevel = headerAbove ? "h3" : "h2";
  const HeadingLevel = headerAbove
    ? hasHeader
      ? "h4"
      : "h3"
    : hasHeader
      ? "h3"
      : "h2";

  const wrapperClass = cn(
    "hbs-component--hero-tease",
    layout !== "text-only" && !isTopper
      ? `hbs-global-align-${align}`
      : "hbs-global-align-center",
  );
  const heroTeaseClass = cn(
    "hbs-hero-tease",
    `hbs-hero-tease--${layout}`,
    isTopper && "hbs-hero-tease--is-topper",
  );
  const titleClass = cn(
    "hbs-hero-tease__title",
    shouldDecreaseTextSize(title, 30) && "hbs-hero-tease__title--long",
  );

  const showHeadshots =
    layout === "side-by-side-media-left" ||
    layout === "side-by-side-media-right" ||
    (layout === "stacked" && byline?.headshots);

  const cardTheme = cn((layout === "card" || layout === "text-only") && theme);

  const presentationalVariant =
    layout === "card"
      ? "card"
      : layout === "text-only"
        ? layout
        : `flat-${layout}`;
  const dataRegion = `cb__hero-tease__${presentationalVariant}`;

  const overlineLabel = overline?.label;
  const overlineLink = overline?.link;

  return (
    <div className={wrapperClass} data-region={dataRegion}>
      {header && (
        <ComponentHeader
          {...header}
          HeadingLevel={componentHeadingLevel}
          borderPosition={header.borderPosition || "bottom"}
        />
      )}
      <div className={heroTeaseClass} data-theme={cardTheme}>
        {/* Media */}
        {layout !== "text-only" && (
          <div className="hbs-hero-tease__media">
            <MediaAsset {...media} aspectRatio="3/2" />
          </div>
        )}
        {/* Content */}
        <div className="hbs-hero-tease__content">
          <div className="hbs-hero-tease__content-inner">
            <div className="hbs-hero-tease__card">
              <div className="hbs-hero-tease__hgroup">
                {overlineLink && link !== overlineLink
                  ? overlineLink &&
                    overlineLabel && (
                      <Link
                        className="hbs-hero-tease__overline"
                        href={overlineLink}
                        editAttributes={editAttributes?.overline}
                      >
                        {overlineLabel}
                      </Link>
                    )
                  : overlineLabel && (
                      <div
                        className="hbs-hero-tease__overline__nolink"
                        {...editAttributes?.overline}
                      >
                        {overlineLabel}
                      </div>
                    )}

                {title && link && (
                  <HeadingLevel
                    className={titleClass}
                    {...editAttributes?.title}
                  >
                    <Link href={link} className="hbs-hero-tease__title-link">
                      {title}
                    </Link>
                  </HeadingLevel>
                )}
              </div>
              <div className="hbs-hero-tease__excerpt">
                {subheading && (
                  <div {...editAttributes?.subheading}>{subheading}</div>
                )}
                {byline && !hideByline && (
                  <div className="hbs-hero-tease__attribution">
                    <Byline
                      {...byline}
                      headshots={showHeadshots}
                      disableLinks
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
